import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { finalize, forkJoin, of } from 'rxjs';
import { normalizeUserRole, UserRole } from '../../core/enums/user-role.enum';
import { HotspotSessionRecord } from '../../core/models/hotspot-session-record.model';
import { Organisation } from '../../core/models/organisation.model';
import { Vehicle } from '../../core/models/vehicle.model';
import { AuthService } from '../../core/services/auth.service';
import { HotspotSessionRecordService } from '../../core/services/hotspot-session-record.service';
import { OrganisationService } from '../../core/services/organisation.service';
import { VehicleService } from '../../core/services/vehicle.service';

type SessionSortDirection = 'asc' | 'desc';
type SessionSortKey = 'passenger' | 'phone' | 'bus' | 'operator' | 'nasIdentifier' | 'startDateTime' | 'active' | 'dataUsage' | 'dataPlan';

@Component({
  selector: 'app-hotspot-session-records',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './hotspot-session-records.component.html',
  styleUrls: ['./hotspot-session-records.component.css']
})
export class HotspotSessionRecordsComponent implements OnInit {
  private authService = inject(AuthService);
  private sessionRecordService = inject(HotspotSessionRecordService);
  private organisationService = inject(OrganisationService);
  private vehicleService = inject(VehicleService);

  records: HotspotSessionRecord[] = [];
  selectedRecord: HotspotSessionRecord | null = null;
  organisations: Organisation[] = [];
  vehicles: Vehicle[] = [];

  searchTerm = '';
  providerFilter = '';
  passengerProfileIDFilter: number | null = null;
  organisationIDFilter: number | null = null;
  vehicleIDFilter: number | null = null;
  nasIdentifierFilter = '';
  activeFilter: 'all' | 'active' | 'ended' = 'all';
  fromFilter = '';
  toFilter = '';

  page = 1;
  perPage = 25;
  total = 0;
  totalPages = 1;

  isAdminOrPlatform = false;
  isOperator = false;
  isLoading = false;
  isLoadingDetail = false;
  errorMessage = '';
  detailError = '';
  sortKey: SessionSortKey = 'startDateTime';
  sortDirection: SessionSortDirection = 'desc';

  ngOnInit(): void {
    const role = this.getCurrentRole();
    this.isAdminOrPlatform = role === UserRole.ADMIN || role === UserRole.PLATFORM_EMPLOYEE;
    this.isOperator = role === UserRole.OPERATOR;
    this.loadReferenceData();
    this.loadRecords();
  }

  loadReferenceData(): void {
    forkJoin({
      organisations: this.isAdminOrPlatform ? this.organisationService.getOrganisations() : of([]),
      vehicles: this.vehicleService.getVehicles()
    }).subscribe({
      next: ({ organisations, vehicles }) => {
        this.organisations = organisations;
        this.vehicles = this.isOperator
          ? vehicles.filter(vehicle => vehicle.organisationID === this.authService.getCurrentUser()?.organisationID)
          : vehicles;
      },
      error: error => {
        console.error('Unable to load session filter reference data:', error);
      }
    });
  }

  loadRecords(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.sessionRecordService.getSessionRecords({
      page: this.page,
      perPage: this.perPage,
      provider: this.providerFilter,
      search: this.searchTerm,
      passengerProfileID: this.passengerProfileIDFilter,
      organisationID: this.isAdminOrPlatform ? this.organisationIDFilter : null,
      vehicleID: this.vehicleIDFilter,
      nasIdentifier: this.nasIdentifierFilter,
      active: this.activeFilter === 'all' ? null : this.activeFilter === 'active',
      from: this.fromFilter,
      to: this.toFilter
    }).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: response => {
        this.records = response.data;
        this.page = response.meta.page;
        this.perPage = response.meta.perPage;
        this.total = response.meta.total;
        this.totalPages = response.meta.totalPages;
      },
      error: error => {
        console.error('Unable to load hotspot session records:', error);
        this.records = [];
        this.total = 0;
        this.totalPages = 1;
        this.errorMessage = error?.error?.message || error?.message || 'Unable to load hotspot session records.';
      }
    });
  }

  get sortedRecords(): HotspotSessionRecord[] {
    return [...this.records].sort((first, second) => {
      const comparison = this.compareSortValues(
        this.getSortValue(first, this.sortKey),
        this.getSortValue(second, this.sortKey)
      );

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  sortBy(key: SessionSortKey): void {
    if (this.sortKey === key) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.sortKey = key;
    this.sortDirection = key === 'startDateTime' || key === 'dataUsage' ? 'desc' : 'asc';
  }

  getSortIcon(key: SessionSortKey): string {
    if (this.sortKey !== key) {
      return 'unfold_more';
    }

    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  getSortLabel(key: SessionSortKey): string {
    if (this.sortKey !== key) {
      return 'Sort';
    }

    return this.sortDirection === 'asc' ? 'Sorted ascending' : 'Sorted descending';
  }

  applyFilters(): void {
    this.page = 1;
    this.loadRecords();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.providerFilter = '';
    this.passengerProfileIDFilter = null;
    this.organisationIDFilter = null;
    this.vehicleIDFilter = null;
    this.nasIdentifierFilter = '';
    this.activeFilter = 'all';
    this.fromFilter = '';
    this.toFilter = '';
    this.applyFilters();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.page) {
      return;
    }

    this.page = page;
    this.loadRecords();
  }

  selectRecord(record: HotspotSessionRecord): void {
    this.isLoadingDetail = true;
    this.detailError = '';

    this.sessionRecordService.getSessionRecord(record.hotspotSessionRecordID).pipe(
      finalize(() => this.isLoadingDetail = false)
    ).subscribe({
      next: detail => {
        this.selectedRecord = detail;
      },
      error: error => {
        console.error('Unable to load hotspot session detail:', error);
        this.detailError = error?.error?.message || error?.message || 'Unable to load hotspot session detail.';
      }
    });
  }

  getPageTitle(): string {
    return this.isOperator ? 'Connected commuter sessions' : 'Hotspot session records';
  }

  getPageDescription(): string {
    return this.isOperator
      ? 'Review synced hotspot sessions connected to your organisation once backend scoping has mapped them to your buses.'
      : 'Review synced hotspot sessions, passenger profile links, and NAS-to-operator/bus mapping context.';
  }

  getPhone(record: HotspotSessionRecord): string {
    return record.passengerPhone || record.phoneNumber || '-';
  }

  getPassengerName(record: HotspotSessionRecord): string {
    return record.passengerDisplayName || 'Unlinked passenger';
  }

  getBusLabel(record: HotspotSessionRecord): string {
    if (!record.vehicleID) {
      return 'Unmapped bus';
    }

    return [record.vehicleRegistration, record.vehicleLabel].filter(Boolean).join(' / ') || `Vehicle ${record.vehicleID}`;
  }

  getDataUsage(record: HotspotSessionRecord): string {
    const total = this.getDataUsageBytes(record);
    if (!total) {
      return '-';
    }

    if (total >= 1024 * 1024 * 1024) {
      return `${(total / 1024 / 1024 / 1024).toFixed(2)} GB`;
    }

    if (total >= 1024 * 1024) {
      return `${(total / 1024 / 1024).toFixed(2)} MB`;
    }

    if (total >= 1024) {
      return `${(total / 1024).toFixed(1)} KB`;
    }

    return `${total} B`;
  }

  private getDataUsageBytes(record: HotspotSessionRecord): number {
    return Number(record.dataIn || 0) + Number(record.dataOut || 0);
  }

  hasActiveFilters(): boolean {
    return Boolean(
      this.searchTerm.trim()
      || this.providerFilter.trim()
      || this.passengerProfileIDFilter
      || this.organisationIDFilter
      || this.vehicleIDFilter
      || this.nasIdentifierFilter.trim()
      || this.activeFilter !== 'all'
      || this.fromFilter
      || this.toFilter
    );
  }

  getPageSummary(): string {
    if (this.total === 0) {
      return '0 sessions';
    }

    const start = ((this.page - 1) * this.perPage) + 1;
    const end = Math.min(this.page * this.perPage, this.total);
    return `${start}-${end} of ${this.total}`;
  }

  formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }

    return new Intl.DateTimeFormat('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(value));
  }

  trackRecord(_index: number, record: HotspotSessionRecord): number {
    return record.hotspotSessionRecordID;
  }

  trackVehicle(_index: number, vehicle: Vehicle): number {
    return vehicle.vehicleID;
  }

  private getSortValue(record: HotspotSessionRecord, key: SessionSortKey): string | number | boolean | null | undefined {
    switch (key) {
      case 'passenger':
        return this.getPassengerName(record);
      case 'phone':
        return this.getPhone(record);
      case 'bus':
        return this.getBusLabel(record);
      case 'operator':
        return record.organisationName || '';
      case 'nasIdentifier':
        return record.nasIdentifier || '';
      case 'startDateTime':
        return record.startDateTime ? new Date(record.startDateTime).getTime() : 0;
      case 'active':
        return record.active ? 1 : 0;
      case 'dataUsage':
        return this.getDataUsageBytes(record);
      case 'dataPlan':
        return record.dataPlan || '';
    }
  }

  private compareSortValues(first: string | number | boolean | null | undefined, second: string | number | boolean | null | undefined): number {
    if (first === second) {
      return 0;
    }

    if (first === null || first === undefined || first === '') {
      return 1;
    }

    if (second === null || second === undefined || second === '') {
      return -1;
    }

    if (typeof first === 'number' && typeof second === 'number') {
      return first - second;
    }

    return String(first).localeCompare(String(second), undefined, {
      numeric: true,
      sensitivity: 'base'
    });
  }

  private getCurrentRole(): UserRole {
    const currentUser = this.authService.getCurrentUser();
    return normalizeUserRole(
      currentUser?.userRoleName
      || currentUser?.userRole
      || currentUser?.userRoleID
      || UserRole.PASSENGER
    );
  }
}
